import { DialogComponent } from '@theme/dialog';

class SimilarItemsDialog extends DialogComponent {}

if (!customElements.get('similar-items-dialog')) {
  customElements.define('similar-items-dialog', SimilarItemsDialog);
}

/**
 * @typedef {'same' | 'similar'} SimilarTab
 * @typedef {'best' | 'popular' | 'price'} SimilarSort
 */

const SECTION_KEY = 'similar_items_modal_grid';
const SECTION_ID_STORAGE_KEY = 'similarItemsSectionId';
const GRID_CACHE_VERSION = '2';

class SimilarItemsController {
  /** @type {SimilarTab} */
  #activeTab = 'similar';

  /** @type {SimilarSort} */
  #activeSort = 'best';

  /** @type {{ productId: string, productHandle: string, collectionHandle: string, productType: string } | null} */
  #context = null;

  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Promise<string> | null} */
  #sectionIdPromise = null;

  #persistSectionId(sectionId) {
    if (!sectionId) return;
    window.__SIMILAR_ITEMS_SECTION_ID = sectionId;
    try {
      sessionStorage.setItem(SECTION_ID_STORAGE_KEY, sectionId);
    } catch {
      /* ignore */
    }
  }

  #readSectionIdFromDom() {
    const config = document.getElementById('similar-items-section-config');
    if (config?.textContent) {
      try {
        const parsed = JSON.parse(config.textContent);
        if (typeof parsed === 'string' && parsed) return parsed;
      } catch {
        /* ignore */
      }
    }

    const sectionElement = document.querySelector('[id*="similar_items_modal_grid"]');
    if (sectionElement?.id) {
      return sectionElement.id.replace(/^shopify-section-/, '');
    }

    if (typeof window.__SIMILAR_ITEMS_SECTION_ID === 'string' && window.__SIMILAR_ITEMS_SECTION_ID) {
      return window.__SIMILAR_ITEMS_SECTION_ID;
    }

    try {
      const stored = sessionStorage.getItem(SECTION_ID_STORAGE_KEY);
      if (stored) return stored;
    } catch {
      /* ignore */
    }

    return '';
  }

  async #resolveSectionId() {
    const existing = this.#readSectionIdFromDom();
    if (existing) {
      this.#persistSectionId(existing);
      return existing;
    }

    if (!this.#sectionIdPromise) {
      this.#sectionIdPromise = this.#bootstrapSectionId();
    }

    return this.#sectionIdPromise;
  }

  async #bootstrapSectionId() {
    if (!this.#context?.productHandle) {
      throw new Error('Similar items section id is unavailable');
    }

    const response = await fetch(`/products/${this.#context.productHandle}`);
    if (!response.ok) {
      throw new Error('Failed to resolve similar items section id');
    }

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const sectionElement = doc.querySelector('[id*="similar_items_modal_grid"]');

    if (!sectionElement?.id) {
      throw new Error('Similar items section id is unavailable');
    }

    const sectionId = sectionElement.id.replace(/^shopify-section-/, '');
    this.#persistSectionId(sectionId);
    return sectionId;
  }

  #getRecommendationsUrl() {
    const dialog = document.getElementById('similar-items-dialog');
    const recommendationsUrl = dialog?.dataset.recommendationsUrl || '/recommendations/products';
    return recommendationsUrl;
  }

  constructor() {
    document.addEventListener('click', this.#handleTriggerClick);

    const dialog = document.getElementById('similar-items-dialog');
    dialog?.querySelectorAll('[data-similar-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        const value = tab.getAttribute('data-similar-tab');
        if (value === 'same' || value === 'similar') {
          this.#setTab(value);
        }
      });
    });

    dialog?.querySelectorAll('[data-sort]').forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.getAttribute('data-sort');
        if (value === 'best' || value === 'popular' || value === 'price') {
          this.#setSort(value);
        }
      });
    });
  }

  /** @param {MouseEvent} event */
  #handleTriggerClick = async (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-similar-items]') : null;
    if (!trigger || !(trigger instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const productCard = trigger.closest('product-card');
    const productId = trigger.dataset.productId || productCard?.dataset.productId || '';
    const productHandle = trigger.dataset.productHandle || productCard?.dataset.productHandle || '';
    const collectionHandle = trigger.dataset.collectionHandle || '';
    const productType = trigger.dataset.productType || '';

    if (!productId) {
      window.location.href = trigger.href;
      return;
    }

    this.#context = { productId, productHandle, collectionHandle, productType };
    await this.open();
  };

  async open() {
    const dialogComponent = document.getElementById('similar-items-dialog');
    const grid = document.getElementById('similar-items-modal-grid');

    if (!(dialogComponent instanceof SimilarItemsDialog) || !grid || !this.#context) return;

    this.#activeTab = 'similar';
    this.#activeSort = 'best';
    this.#syncTabUi();
    this.#syncSortUi();
    grid.innerHTML = '<p class="similar-items-modal__loading">Loading…</p>';

    dialogComponent.showDialog();
    await this.#renderGrid();
  }

  /** @param {SimilarTab} tab */
  #setTab(tab) {
    this.#activeTab = tab;
    this.#syncTabUi();
    this.#renderGrid();
  }

  /** @param {SimilarSort} sort */
  #setSort(sort) {
    this.#activeSort = sort;
    this.#syncSortUi();
    this.#sortGrid();
  }

  #syncTabUi() {
    const dialog = document.querySelector('.similar-items-modal');
    dialog?.setAttribute('data-active-tab', this.#activeTab);

    document.querySelectorAll('[data-similar-tab]').forEach((tab) => {
      const isActive = tab.getAttribute('data-similar-tab') === this.#activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    document.querySelector('[data-similar-toolbar]')?.toggleAttribute('hidden', this.#activeTab !== 'same');
  }

  #syncSortUi() {
    document.querySelectorAll('[data-sort]').forEach((button) => {
      button.classList.toggle('is-active', button.getAttribute('data-sort') === this.#activeSort);
    });
  }

  async #renderGrid() {
    const grid = document.getElementById('similar-items-modal-grid');
    if (!grid || !this.#context) return;

    grid.innerHTML = '<p class="similar-items-modal__loading">Loading…</p>';

    try {
      const html = await this.#loadGridHtml();
      if (!html) {
        grid.innerHTML = '<p class="similar-items-modal__empty">No products found.</p>';
        return;
      }

      grid.innerHTML = html;
      this.#removeCurrentProduct(grid);
      this.#disableModalCardHovers(grid);

      if (!grid.querySelector('.resource-list__item')) {
        grid.innerHTML = '<p class="similar-items-modal__empty">No products found.</p>';
        return;
      }

      this.#sortGrid();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      grid.innerHTML = '<p class="similar-items-modal__empty">Unable to load products.</p>';
    }
  }

  async #loadGridHtml() {
    if (!this.#context) return '';

    const cacheKey = `${GRID_CACHE_VERSION}:${this.#activeTab}:${this.#context.productId}:${this.#context.collectionHandle}`;
    const cached = this.#cache.get(cacheKey);
    if (cached) return cached;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const sectionId = await this.#resolveSectionId();

    let url = '';

    if (this.#activeTab === 'similar') {
      const recommendationsUrl = this.#getRecommendationsUrl();
      url = `${recommendationsUrl}?product_id=${this.#context.productId}&limit=30&section_id=${encodeURIComponent(sectionId)}&intent=related`;
    } else if (this.#context.collectionHandle) {
      url = `/collections/${this.#context.collectionHandle}?section_id=${encodeURIComponent(sectionId)}`;
    } else if (this.#context.productType) {
      url = `/search?q=${encodeURIComponent(this.#context.productType)}&type=product&section_id=${encodeURIComponent(sectionId)}`;
    } else {
      url = `/collections/all?section_id=${encodeURIComponent(sectionId)}`;
    }

    if (!url) return '';

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error('Failed to load products');

    const html = this.#extractGridHtml(await response.text());
    this.#cache.set(cacheKey, html);
    return html;
  }

  /** @param {string} htmlText */
  #extractGridHtml(htmlText) {
    const doc = new DOMParser().parseFromString(htmlText, 'text/html');
    const grid = doc.querySelector('.similar-items-modal-grid');

    if (grid) {
      grid.querySelector('#similar-items-section-config')?.remove();
      return grid.outerHTML;
    }

    const section = doc.querySelector('[id*="similar_items_modal_grid"]');
    const sectionGrid = section?.querySelector('.similar-items-modal-grid');

    if (sectionGrid) {
      sectionGrid.querySelector('#similar-items-section-config')?.remove();
      return sectionGrid.outerHTML;
    }

    return section?.innerHTML.trim() || '';
  }

  /** @param {HTMLElement} grid */
  #removeCurrentProduct(grid) {
    if (!this.#context) return;

    grid.querySelectorAll('product-card[data-product-id]').forEach((card) => {
      if (String(card.getAttribute('data-product-id')) === String(this.#context?.productId)) {
        card.closest('.resource-list__item')?.remove();
      }
    });
  }

  /** @param {HTMLElement} grid */
  #disableModalCardHovers(grid) {
    grid.querySelectorAll('.product-card__hover-actions, .ae-love-card__hover-actions').forEach((actions) => actions.remove());
    grid.querySelectorAll('[data-product-preview], [data-similar-items]').forEach((link) => link.remove());
  }

  #sortGrid() {
    if (this.#activeTab !== 'same') return;

    const grid = document.getElementById('similar-items-modal-grid');
    const list = grid?.querySelector('.similar-items-modal-grid__list, .resource-list--grid');
    if (!grid || !list || this.#activeSort === 'best') return;

    const items = [...list.querySelectorAll('.resource-list__item')];

    items.sort((a, b) => {
      if (this.#activeSort === 'price') {
        return this.#readCardPrice(a) - this.#readCardPrice(b);
      }

      const titleA = a.querySelector('[ref="productTitleLink"]')?.textContent?.trim() || '';
      const titleB = b.querySelector('[ref="productTitleLink"]')?.textContent?.trim() || '';
      return titleA.localeCompare(titleB);
    });

    items.forEach((item) => list.appendChild(item));
  }

  /** @param {Element} item */
  #readCardPrice(item) {
    const priceText =
      item.querySelector('.price')?.textContent ||
      item.querySelector('[ref="priceContainer"]')?.textContent ||
      '';
    const numeric = Number(priceText.replace(/[^\d.]/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
  }
}

new SimilarItemsController();
